<template>
  <div id="app">
    <v-app v-if="$vuetify.breakpoint.name == 'xs'"> <MobileVersion /> </v-app>
    <v-app
      :style="{
        display: $vuetify.breakpoint.name == 'xs' ? 'none' : 'inline',
      }"
    >
      <Menu
        v-if="!showOnboarding && !isLoading"
        @toggle-music="toggleMusic"
        @change-music-theme="changeTheme"
        :musicOn="musicOn"
      />
      <transition v-if="showOnboarding" mode="out-in" name="fade">
        <Onboarding
          @start-sound="
            startSound();
            showOnboarding = false;
          "
          @toggle-music="toggleMusic"
        />
      </transition>
      <Loading v-if="isLoading" />
      <router-view
        style="z-index: 999"
        @toggle-music="toggleMusic"
        :players="birdPlayers"
        :musicOn="musicOn"
      />
      <Scene
        v-if="glbdata"
        ref="scene"
        @loaded="onLoaded"
        @loading="isLoading = true"
        @toggle-music="toggleMusic"
        :showOnboarding="showOnboarding"
        :isLoading="isLoading"
        :players="birdPlayers"
        :glbdata="glbdata"
        :musicOn="musicOn"
        style="z-index: 990"
        :style="{
          display: showScene ? 'inline' : 'none',
        }"
      />
    </v-app>
  </div>
</template>

<script>
import * as Tone from "tone";
import Menu from "./components/Menu";
import MobileVersion from "./components/MobileVersion.vue";
import Onboarding from "./components/Onboarding.vue";
import birdsData from "./bird_infos.json";
import Loading from "./components/Loading.vue";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import Scene from "./components/Scene.vue";
import router from "./router/index";

export default {
  name: "App",
  components: { Menu, Onboarding, Loading, Scene, MobileVersion },
  data() {
    return {
      isLoading: true,
      experienceStarted: false,
      isDay: true,
      showOnboarding: false,
      musicOn: true,
      birdPlayers: null,
      dayPlayer: null,
      nightPlayer: null,
      birds: birdsData,
      glbdata: null,
    };
  },
  mounted() {
    document.title = "Birdwatcher";

    this.$route.name !== "Main" ? router.push({ name: "Main" }) : null;
    Tone.start();
    this.dayPlayer = new Tone.Player(
      require("./assets/theme_day.mp3")
    ).toDestination();
    this.dayPlayer.loop = true;
    this.dayPlayer.fadeOut = 1;
    this.dayPlayer.fadeIn = 1;

    this.nightPlayer = new Tone.Player(
      require("./assets/theme_night.mp3")
    ).toDestination();
    this.nightPlayer.loop = true;
    this.nightPlayer.fadeOut = 1;
    this.nightPlayer.fadeIn = 1;

    const options = {};
    this.birds.forEach((bird) => {
      var buffer = new Tone.ToneAudioBuffer(
        require("./assets/bird-sounds/" + bird.url + ".mp3")
      );
      options[bird.url] = buffer;
    });
    this.birdPlayers = new Tone.Players(options).toDestination();
    this.loadGlbData();
  },
  computed: {
    showScene() {
      if (this.$route.name == "Main") {
        return true;
      }
      return false;
    },
  },
  methods: {
    startSound() {
      if (this.musicOn) {
        Tone.loaded().then(() => {
          this.dayPlayer.start();
        });
      }
    },
    toggleMusic() {
      if (this.musicOn) {
        this.isDay
          ? (this.dayPlayer.mute = true)
          : (this.nightPlayer.mute = true);
      } else {
        this.isDay
          ? (this.dayPlayer.mute = false)
          : (this.nightPlayer.mute = false);
      }
      this.musicOn = !this.musicOn;
    },
    changeTheme() {
      this.$refs.scene.changeSun();
      if (this.isDay) {
        this.dayPlayer.stop();
        this.nightPlayer.start();
        this.musicOn ? null : (this.nightPlayer.mute = true);
      } else {
        this.nightPlayer.stop();
        this.dayPlayer.start();
        this.musicOn ? null : (this.dayPlayer.mute = true);
      }

      this.isDay = !this.isDay;
    },
    onLoaded() {
      this.isLoading = false;
      if (!this.experienceStarted) {
        this.experienceStarted = true;
        this.showOnboarding = true;
      }
    },
    async loadGlbData() {
      const loader = new GLTFLoader();
      const [
        birchTrunksData,
        birchesData,
        boldTreesData2,
        boldTreesData3,
        boldTreesData4,
        boldTreesData,
        bushesData2,
        bushesIslandData,
        bushesData,
        grassData,
        landscapeData,
        leavesData,
        pinesData2,
        pinesData3,
        pinesData,
        pineTrunksData,
        reedsData,
        treeLeavesData2,
        treeLeavesData,
        treeData,
        treesData,
        treesData2,
      ] = await Promise.all([
        loader.loadAsync("objects/birch_trunks.glb"),
        loader.loadAsync("objects/birches.glb"),
        loader.loadAsync("objects/bold_trees_2.glb"),
        loader.loadAsync("objects/bold_trees_3.glb"),
        loader.loadAsync("objects/bold_trees_4.glb"),
        loader.loadAsync("objects/bold_trees.glb"),
        loader.loadAsync("objects/bushes_2.glb"),
        loader.loadAsync("objects/bushes_island.glb"),
        loader.loadAsync("objects/bushes.glb"),
        loader.loadAsync("objects/grass.glb"),
        loader.loadAsync("objects/landscape.glb"),
        loader.loadAsync("objects/leaves.glb"),
        loader.loadAsync("objects/pine_trees_2.glb"),
        loader.loadAsync("objects/pine_trees_3.glb"),
        loader.loadAsync("objects/pine_trees.glb"),
        loader.loadAsync("objects/pine_trunks.glb"),
        loader.loadAsync("objects/reeds.glb"),
        loader.loadAsync("objects/tree_leaves_2.glb"),
        loader.loadAsync("objects/tree_leaves.glb"),
        loader.loadAsync("objects/tree_trunks.glb"),
        loader.loadAsync("objects/trees.glb"),
        loader.loadAsync("objects/trees_2.glb"),
      ]);
      this.glbdata = {
        birchTrunksData: birchTrunksData,
        birchesData: birchesData,
        boldTreesData2: boldTreesData2,
        boldTreesData3: boldTreesData3,
        boldTreesData4: boldTreesData4,
        boldTreesData: boldTreesData,
        bushesData2: bushesData2,
        bushesIslandData: bushesIslandData,
        bushesData: bushesData,
        grassData: grassData,
        landscapeData: landscapeData,
        leavesData: leavesData,
        pinesData2: pinesData2,
        pinesData3: pinesData3,
        pinesData: pinesData,
        pineTrunksData: pineTrunksData,
        reedsData: reedsData,
        treeLeavesData2: treeLeavesData2,
        treeLeavesData: treeLeavesData,
        treeData: treeData,
        treesData: treesData,
        treesData2: treesData2,
      };
    },
  },
};
</script>

<style>
@font-face {
  font-family: MabryBlackItalic;
  src: url("./assets/Mabry/mabry-black-italic-pro.ttf");
}
@font-face {
  font-family: MabryBlack;
  src: url("./assets/Mabry/mabry-black-pro.ttf");
}
@font-face {
  font-family: MabryBoldItalic;
  src: url("./assets/Mabry/mabry-bold-italic-pro.ttf");
}
@font-face {
  font-family: MabryBold;
  src: url("./assets/Mabry/mabry-bold-pro.ttf");
}
@font-face {
  font-family: MabryItalic;
  src: url("./assets/Mabry/mabry-italic-pro.ttf");
}
@font-face {
  font-family: MabryLightItalic;
  src: url("./assets/Mabry/mabry-light-italic-pro.ttf");
}
@font-face {
  font-family: MabryLight;
  src: url("./assets/Mabry/mabry-light-pro.ttf");
}
@font-face {
  font-family: MabryMediumItalic;
  src: url("./assets/Mabry/mabry-medium-italic-pro.ttf");
}
@font-face {
  font-family: MabryMedium;
  src: url("./assets/Mabry/mabry-medium-pro.ttf");
}
@font-face {
  font-family: MabryRegular;
  src: url("./assets/Mabry/mabry-regular-pro.ttf");
}
#app {
  font-family: "MabryRegular";
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  margin-top: 60px;
}
.fade-leave-active {
  transition: opacity 1.5s ease-in;
}

.fade-leave-to {
  opacity: 0;
}
.fade-leave {
  opacity: 1;
}
</style>
